import React, { useEffect, useState } from "react";
import { Row, Col, Button, Collapse, Icon, Form, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import SelectInput from "../CommonFormItems/SelectInput/SelectInput";
import { products, serviceTypes } from "../../../utils/formOptions";
import { userProfiles } from "../../../redux/slices/UserProfilesSlice";
import { preRegisterDetails } from "../../../redux/slices/PreregisterDetailsSlice";
import {
  productProfileCorrespondences,
  checkProximityRole,
} from "../../../utils/userRolesUtils";
import useSenders from "../../../hooks/useSenders";
import useRecipients from "../../../hooks/useRecipients";
import ParticipantBlock from "./ParticipantBlock/ParticipantBlock";
import ShipmentBlock from "./ShipmentBlock/ShipmentBlock";
import usePreregister from "../../../hooks/usePreRegister";
import {
  participantsDetails,
  resetSenderParticipantDetails,
  resetRecipientParticipantDetails,
} from "../../../redux/slices/ParticipantsDetailsSlice";
import { resetParticipantDetails } from "../../../utils/participantUtils";
import { checkContractOptionCanDoSpecialDestination } from "../../../utils/contractUtils";
import SwitchInput from "../CommonFormItems/SwitchInput/SwitchInput";
import MassivePreRegistrationBlock from "./MassivePreRegistrationBlock/MassivePreRegistrationBlock";
import usePostalCode from "../../../hooks/usePostalCode";

const { Panel } = Collapse;

const triggerRecipientNotAllowed = (context) => {
  const { setFieldsValue, dispatch } = context;
  // notification.error({
  //   message: "Error",
  //   description:
  //     "La información de contrato proporcionada no permite el envío al destinatario seleccionado. Se procede a borrar datos de destinatario.",
  // });
  const resetDetails = resetParticipantDetails("recipient", false, undefined);
  setFieldsValue(resetDetails);
  dispatch(resetRecipientParticipantDetails());
};

const fieldDependencies = {
  fieldDependencyInitializer: {
    affects: ["serviceType"],
    onChange: (value, context) => {
      const {
        setter,
        services,
        setFieldsValue,
        handleDependencyChange,
        hasProximityProfile,
      } = context;
      resetFields(
        [
          "serviceType",
          "productType",
          "contractNumber",
          "clientNumber",
          "labellerCode",
        ],
        setter,
        setFieldsValue
      );

      const serviceOptions = getServicesOptions(services, hasProximityProfile);
      if (serviceOptions.length <= 1) {
        const [onlyServiceOption] = serviceOptions;
        setFieldsValue({
          serviceType: onlyServiceOption,
        });
      }

      setter((prevState) => ({
        ...prevState,
        serviceTypeOptions: serviceOptions,
      }));
      handleDependencyChange(null, "serviceType");
    },
  },
  serviceType: {
    affects: ["productType"],
    onChange: (value, context) => {
      const {
        setter,
        info,
        setFieldsValue,
        handleDependencyChange,
        dispatch,
        onColdchainChange,
      } = context;
      resetFields(
        ["productType", "contractNumber", "clientNumber", "labellerCode"],
        setter,
        setFieldsValue
      );

      if (typeof onColdchainChange === "function") {
        onColdchainChange(false);
      }

      dispatch(resetSenderParticipantDetails());
      dispatch(resetRecipientParticipantDetails());

      const resetSenderFields = resetParticipantDetails(
        "sender",
        false,
        undefined
      );
      const resetRecipientFields = resetParticipantDetails(
        "recipient",
        false,
        undefined
      );

      setFieldsValue({
        ...resetSenderFields,
        ...resetRecipientFields,
      });

      if (value === "Frio Proximidad") {
        handleFrioProximidadSelected(setFieldsValue, setter);

        const filteredInfo = {
          ...info,
          data: info.data.filter(
            (profile) => profile.profileCod === "CFRIO-PROXIMIDAD"
          ),
        };

        const contractOptions = getContractOptions(filteredInfo, undefined);

        setter((prevState) => ({
          ...prevState,
          contractNumberOptions: contractOptions,
        }));

        if (contractOptions.length <= 1) {
          const [onlyContractOption] = contractOptions;
          setFieldsValue({
            contractNumber: onlyContractOption,
          });
          handleDependencyChange(onlyContractOption, "contractNumber");
        }

        return;
      }

      const productOptions = getProductsOptions(info);

      setter((prevState) => ({
        ...prevState,
        productTypeOptions: productOptions,
      }));
      if (productOptions.length <= 1) {
        const [onlyProductOption] = productOptions;

        setFieldsValue({
          productType: onlyProductOption,
        });
        handleDependencyChange(onlyProductOption, "productType");
      }
    },
  },
  productType: {
    affects: ["contractNumber"],
    onChange: (value, context) => {
      const {
        setter,
        info,
        setFieldsValue,
        handleDependencyChange,
        getFieldsValue,
        participantsDetails,
        onColdchainChange,
      } = context;
      resetFields(
        ["contractNumber", "clientNumber", "labellerCode"],
        setter,
        setFieldsValue
      );
      if (!value) {
        if (typeof onColdchainChange === "function") {
          onColdchainChange(false);
        }
        return;
      }

      const isColdchain = value?.key?.includes("PADXA");
      if (typeof onColdchainChange === "function") {
        onColdchainChange(isColdchain);
      }

      const contractOptions = getContractOptions(info, value);
      setter((prevState) => ({
        ...prevState,
        contractNumberOptions: contractOptions,
      }));

      if (contractOptions.length <= 1) {
        const [onlyContractOption] = contractOptions;
        setFieldsValue({
          contractNumber: onlyContractOption,
        });
        handleDependencyChange(onlyContractOption, "contractNumber");
      }

      const contractOptionsCanDoSpecialDestination =
        checkContractOptionCanDoSpecialDestination(
          contractOptions,
          info,
          "contractNumber"
        );

      if (!contractOptionsCanDoSpecialDestination) {
        triggerRecipientNotAllowed(context);
        return false;
      }
    },
  },
  contractNumber: {
    affects: ["clientNumber"],
    onChange: (value, context) => {
      const {
        setter,
        info,
        setFieldsValue,
        handleDependencyChange,
        getFieldsValue,
      } = context;
      resetFields(["clientNumber", "labellerCode"], setter, setFieldsValue);
      if (!value) {
        return;
      }

      const clientOptions = getClientOptions(info, value);
      setter((prevState) => ({
        ...prevState,
        clientNumberOptions: clientOptions,
      }));
      if (clientOptions.length <= 1) {
        const [onlyClientOption] = clientOptions;
        setFieldsValue({
          clientNumber: onlyClientOption,
        });
        handleDependencyChange(onlyClientOption, "clientNumber");
      }

      const contractOptionsCanDoSpecialDestination =
        checkContractOptionCanDoSpecialDestination(
          clientOptions,
          info,
          "clientNumber"
        );
      if (!contractOptionsCanDoSpecialDestination) {
        triggerRecipientNotAllowed(context);
        return false;
      }
    },
  },
  clientNumber: {
    affects: ["labellerCode"],
    onChange: (value, context) => {
      const { setter, info, setFieldsValue, handleDependencyChange } = context;
      resetFields(["labellerCode"], setter, setFieldsValue);
      if (!value) {
        return;
      }
      const labellerOptions = getLabellerOptions(info, value);
      setter((prevState) => ({
        ...prevState,
        labellerCodeOptions: labellerOptions,
      }));
      if (labellerOptions.length <= 1) {
        const [onlyLabellerOption] = labellerOptions;
        setFieldsValue({
          labellerCode: onlyLabellerOption,
        });
      }
      const contractOptionsCanDoSpecialDestination =
        checkContractOptionCanDoSpecialDestination(
          labellerOptions,
          info,
          "labellerCode"
        );

      if (!contractOptionsCanDoSpecialDestination) {
        triggerRecipientNotAllowed(context);
        return false;
      }
    },
  },
  labellerCode: {
    affects: ["labellerCodeOnSelect"],
    onChange: (value, context) => {
      const { setter, info, setFieldsValue, handleDependencyChange } = context;
      if (!value) {
        return;
      }

      for (let profile of info.data) {
        if (
          profile?.labellerCode === value &&
          profile?.profileCod?.includes("DEST_ESPECIAL")
        ) {
          return true;
        }
      }
      triggerRecipientNotAllowed(context);
    },
  },
};

function resetFields(fields, setter, setFieldsValue) {
  let resetFields = {};
  let resetOptions = {};
  for (let field of fields) {
    resetFields[field] = undefined;
    resetOptions[`${field}Options`] = [];
  }
  setter((prevState) => ({
    ...prevState,
    ...resetOptions,
  }));
  setFieldsValue(resetFields);
}

function getServicesOptions(services, hasProximityProfile) {
  const availableServices = [];

  for (let service of services) {
    if (service.label === "Frio Proximidad" && !hasProximityProfile) {
      continue;
    }
    availableServices.push(service?.label);
  }
  return availableServices;
}

function getProductsOptions(userRoles) {
  const availableProducts = [];

  if (
    userRoles?.data?.findIndex(
      (role) => role.profileCod === "CFRIO-PAQ_FRIO"
    ) !== -1
  ) {
    const paqFrioDetails = products.find((product) =>
      product.key.includes("FRAXQ")
    );
    availableProducts.push(paqFrioDetails);
  }

  if (
    userRoles?.data?.findIndex(
      (role) => role.profileCod === "CFRIO-PAQ_FARMA"
    ) !== -1
  ) {
    const paqFarmaDetails = products.find((product) =>
      product.key.includes("FRAXS")
    );
    availableProducts.push(paqFarmaDetails);
  }

  if (
    userRoles?.data?.findIndex(
      (role) => role.profileCod === "CFRIO-PK_COLDCHAIN"
    ) !== -1
  ) {
    const coldChainDetails = products.find((product) =>
      product.key.includes("PADXA")
    );

    availableProducts.push(coldChainDetails);
  }
  return availableProducts;
}

function getContractOptions(userRoles, product) {
  let contracts = [];
  if (!userRoles || !userRoles?.data) {
    return contracts;
  }

  if (!product) {
    for (let role of userRoles?.data) {
      if (!contracts.includes(role?.contractNumber)) {
        contracts.push(role?.contractNumber);
      }
    }
    return contracts;
  }

  let filteredUserRoles = userRoles?.data?.filter(
    (profile) =>
      profile?.profileCod === productProfileCorrespondences[product.label]
  );
  filteredUserRoles.forEach((item) => {
    if (!contracts.includes(item?.contractNumber)) {
      contracts.push(item.contractNumber);
    }
  });

  return contracts;
}

function getClientOptions(userRoles, contract) {
  let clients = [];
  if (!userRoles) {
    return clients;
  }

  if (!contract) {
    for (let role of userRoles?.data) {
      if (!clients.includes(role?.clientNumber)) {
        clients.push(role.clientNumber);
      }
    }

    return clients;
  }

  let filteredUserRoles = userRoles?.data?.filter(
    (profile) => profile?.contractNumber === contract
  );

  filteredUserRoles.forEach((item) => {
    if (!clients.includes(item?.clientNumber)) {
      clients.push(item.clientNumber);
    }
  });

  return clients;
}

function getLabellerOptions(userRoles, client) {
  const labellers = [];
  if (!userRoles) {
    return labellers;
  }

  if (!client) {
    for (let role of userRoles?.data) {
      if (!labellers.includes(role?.labellerCode)) {
        labellers.push(role.labellerCode);
      }
    }
  }

  let filteredUserRoles = userRoles?.data?.filter(
    (profile) => profile.clientNumber === client
  );

  filteredUserRoles.forEach((item) => {
    if (!labellers.includes(item?.labellerCode)) {
      labellers.push(item.labellerCode);
    }
  });

  return labellers;
}

const shouldShowProductType = (service) =>
  service && service.trim().toLowerCase() !== "frio proximidad";

function handleFrioProximidadSelected(setFieldsValue, setter) {
  setFieldsValue({ productType: undefined });
  setter((prevState) => ({
    ...prevState,
    productTypeOptions: [],
  }));
}

function PreRegistrationForm({
  form,
  callback,
  preRegistrationDetails,
  onColdchainChange,
  searchOptions,
}) {
  const { getFieldDecorator, setFieldsValue, getFieldsValue } = form;
  const reduxUserProfiles = useSelector(userProfiles);
  const productOptions = getProductsOptions(reduxUserProfiles);
  const { handlePreregisterSteps } = usePreregister();
  const { getRecipients } = useRecipients();
  const { getSenders } = useSenders();
  const { getPostalCodeRestriction } = usePostalCode();
  const reduxPreRegisterDetails = useSelector(preRegisterDetails);
  const reduxParticipantsDetails = useSelector(participantsDetails);
  const dispatch = useDispatch();
  const hasProximityProfile = checkProximityRole(reduxUserProfiles?.data);

  const [contractDetails, setContractDetails] = useState({
    serviceTypeOptions: [],
    productTypeOptions: productOptions,
    contractNumberOptions: [],
    clientNumberOptions: [],
    labellerCodeOptions: [],
  });

  const [fileUploaded, setFileUploaded] = useState({
    file: null,
    fileBase64: null,
    specialDestination: false,
  });

  const [postalCodeRestrictions, setPostalCodeRestrictions] = useState([]);
  const [validatedPostalCodes, setValidatedPostalCodes] = useState({
    sender: null,
    recipient: null,
  });

  useEffect(() => {
    handleDependencyChange(null, "fieldDependencyInitializer");
  }, []);

  const handleDependencyChange = (value, name) => {
    const dependency = fieldDependencies[name];
    if (dependency && typeof dependency.onChange === "function") {
      const context = {
        state: contractDetails,
        setter: setContractDetails,
        info: reduxUserProfiles,
        services: serviceTypes,
        setFieldsValue: setFieldsValue,
        getFieldsValue: getFieldsValue,
        handleDependencyChange: handleDependencyChange,
        participantsDetails: reduxParticipantsDetails,
        dispatch: dispatch,
        hasProximityProfile: hasProximityProfile,
        onColdchainChange,
      };
      dependency.onChange(value, context);
    }
  };

  const serviceTypeValue = form.getFieldValue("serviceType");
  const productTypeValue = form.getFieldValue("productType");

  const handleGetPostalCodeRestriction = async (postalCode) => {
    const result = await getPostalCodeRestriction(postalCode);
    setPostalCodeRestrictions(result || []);
    return result || [];
  };

  const handlePostalCodeValidated = (type, postalCode) => {
    setValidatedPostalCodes((prev) => ({
      ...prev,
      [type]: postalCode,
    }));
  };

  const contractFormInfo = {
    contractNumber: form.getFieldValue("contractNumber"),
    clientNumber: form.getFieldValue("clientNumber"),
    labellerCode: form.getFieldValue("labellerCode"),
  };

  const handleSpecialRoleDetected = (hasSpecialRole) => {
    setFileUploaded((prev) => ({
      ...prev,
      specialDestination: hasSpecialRole,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        const finalValues = { ...values };
        if (values.massivePreRegistration && fileUploaded.fileBase64) {
          finalValues.file = fileUploaded.fileBase64;
          finalValues.specialDestination = fileUploaded.specialDestination;
        }
        callback(finalValues, setFieldsValue);
      }
    });
  };

  return (
    <div className="PreRegistrationForm">
      <Form onSubmit={handleFormSubmit}>
        <Row className="form-section">
          <Collapse
            defaultActiveKey={["1"]}
            expandIcon={({ isActive }) => (
              <Icon type="caret-right" rotate={isActive ? 90 : 0} />
            )}
          >
            <Panel
              header="Servicio/Producto"
              key="1"
              extra={<Icon type="minus-circle" />}
              className="Busca"
            >
              <Row type="flex" justify="space-between" gutter={[16, 16]}  className="pr-grid-row">
                  <Col xs={24} md={7} className="pr-col">
                  <SelectInput
                    getFieldDecorator={getFieldDecorator}
                    label="Tipo de servicio"
                    name="serviceType"
                    options={contractDetails.serviceTypeOptions}
                    allowClear={true}
                    disabled={serviceTypes?.length < 2}
                    placeholder="Seleccione un servicio..."
                    required
                    callback={handleDependencyChange}
                  />
                </Col>
                {shouldShowProductType(serviceTypeValue) ? (
                  <Col xs={24} md={7} className="pr-col pr-col-offset">
                    <SelectInput
                      getFieldDecorator={getFieldDecorator}
                      label="Tipo de producto"
                      name="productType"
                      options={contractDetails.productTypeOptions}
                      allowClear={true}
                      disabled={contractDetails?.productTypeOptions?.length < 1}
                      callback={handleDependencyChange}
                      // initialValue={productsInitialValue}
                      placeholder="Seleccione un producto..."
                      labelInValue
                      required
                    />
                  </Col>
                ) : (
                  <Col xs={24} md={7} className="pr-col pr-col-offset"></Col>
                )}
                <Col xs={24} md={7} className="pr-col pr-col-offset"></Col>
              </Row>
            </Panel>
          </Collapse>
        </Row>
        <Row className="form-section">
          <Collapse
            defaultActiveKey={["1"]}
            expandIcon={({ isActive }) => (
              <Icon type="caret-right" rotate={isActive ? 90 : 0} />
            )}
          >
            <Panel
              header="Contrato"
              key="1"
              extra={<Icon type="minus-circle" />}
              className="Busca"
            >
              <Row type="flex" justify="space-between" gutter={[16, 16]}  className="pr-grid-row">
                  <Col xs={24} md={7} className="pr-col">
                  <SelectInput
                    getFieldDecorator={getFieldDecorator}
                    label="Contrato"
                    name="contractNumber"
                    options={contractDetails?.contractNumberOptions}
                    allowClear={true}
                    disabled={
                      contractDetails.contractNumberOptions?.length <= 1
                    }
                    callback={handleDependencyChange}
                    placeholder="Seleccione un contrato..."
                    required
                  />
                </Col>
                <Col xs={24} md={7} className="pr-col pr-col-offset">
                  <SelectInput
                    getFieldDecorator={getFieldDecorator}
                    label="Detallable"
                    name="clientNumber"
                    options={contractDetails?.clientNumberOptions}
                    allowClear={true}
                    disabled={contractDetails?.clientNumberOptions?.length <= 1}
                    callback={handleDependencyChange}
                    placeholder="Seleccione un detallable..."
                    required
                  />
                </Col>
                <Col xs={24} md={7} className="pr-col pr-col-offset">
                  <SelectInput
                    getFieldDecorator={getFieldDecorator}
                    label="Etiquetador"
                    name="labellerCode"
                    options={contractDetails.labellerCodeOptions}
                    allowClear={true}
                    callback={handleDependencyChange}
                    disabled={contractDetails?.labellerCodeOptions?.length <= 1}
                    placeholder="Seleccione un etiquetador..."
                    required
                  />
                </Col>
              </Row>
            </Panel>
          </Collapse>
        </Row>

        <ParticipantBlock
          form={form}
          getParticipants={getSenders}
          participantType="sender"
          requiredFields={reduxPreRegisterDetails}
          serviceType={serviceTypeValue}
          getPostalCodeRestriction={handleGetPostalCodeRestriction}
          onPostalCodeValidated={handlePostalCodeValidated}
        />
        <Row type="flex" justify="end">
          <Col>
            <SwitchInput
              getFieldDecorator={getFieldDecorator}
              setFieldsValue={setFieldsValue}
              label="Pre-registro masivo"
              size="small"
              name="massivePreRegistration"
            />
          </Col>
        </Row>

        {form.getFieldValue("massivePreRegistration") ? (
          <MassivePreRegistrationBlock
            serviceType={serviceTypeValue}
            productType={productTypeValue}
            onFileBase64={(base64) =>
              setFileUploaded((prev) => ({ ...prev, fileBase64: base64 }))
            }
            fileUploaded={fileUploaded}
            setFileUploaded={setFileUploaded}
            onSpecialRoleDetected={handleSpecialRoleDetected}
            contractFormInfo={contractFormInfo}
          />
        ) : (
          <>
            <ParticipantBlock
              form={form}
              getParticipants={getRecipients}
              participantType="recipient"
              serviceType={serviceTypeValue}
              getPostalCodeRestriction={handleGetPostalCodeRestriction}
              onPostalCodeValidated={handlePostalCodeValidated}
            />
            <ShipmentBlock
              form={form}
              serviceType={serviceTypeValue}
              productType={productTypeValue}
              postalCodeRestrictions={postalCodeRestrictions}
              validatedPostalCodes={validatedPostalCodes}
              searchOptions={searchOptions}
            />
          </>
        )}
        <Row type="flex" justify="space-between" gutter={[16, 16]}  className="button-container">
          <Button
            htmlType="submit"
            className="action-btn"
            disabled={preRegistrationDetails?.isLoading}
          >
            {preRegistrationDetails?.isLoading
              ? "Generando..."
              : "Generar envío/expedición"}
          </Button>
        </Row>
      </Form>
    </div>
  );
}

export default Form.create()(PreRegistrationForm);
